/**
 * Square icon-only button, used in card footers and toolbars.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import type { JSX } from 'react';

export type IconButtonVariant = 'neutral' | 'outlined' | 'primary' | 'subtle' | 'danger';

const VARIANT_STYLE: Record<
  IconButtonVariant,
  { background: string; color: string; border: string; hover: string }
> = {
  neutral: {
    background: 'transparent',
    color: 'var(--color-text-secondary)',
    border: 'none',
    hover: 'var(--color-surface-hover)',
  },
  outlined: {
    background: 'var(--color-surface)',
    color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-border-strong)',
    hover: 'var(--color-surface-hover)',
  },
  primary: {
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    border: 'none',
    hover: 'var(--color-primary-hover)',
  },
  subtle: {
    background: 'var(--color-primary-subtle)',
    color: 'var(--color-primary)',
    border: 'none',
    hover: 'var(--gl-blue-25)',
  },
  danger: {
    background: 'transparent',
    color: 'var(--color-danger)',
    border: 'none',
    hover: 'var(--color-danger-subtle)',
  },
};

const DIMENSION = { sm: 32, md: 40, lg: 48 } as const;
const ICON_SIZE = { sm: 18, md: 20, lg: 22 } as const;

export interface IconButtonProps {
  icon: IGLIcon['variant'];
  /** Required: the button has no text, so it needs an accessible name. */
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: keyof typeof DIMENSION;
  disabled?: boolean;
  onClick?: () => void;
  id?: string;
}

export const IconButton = ({
  icon,
  'aria-label': ariaLabel,
  variant = 'neutral',
  size = 'md',
  disabled = false,
  onClick,
  id,
}: IconButtonProps): JSX.Element => {
  const style = VARIANT_STYLE[variant];

  return (
    <button
      id={id}
      type='button'
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: ${DIMENSION[size]}px;
        height: ${DIMENSION[size]}px;
        border-radius: var(--radius-control);
        background: ${style.background};
        color: ${style.color};
        border: ${style.border};
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        opacity: ${disabled ? 0.45 : 1};
        transition: background var(--duration-fast) var(--ease-standard);

        &:hover:not(:disabled) {
          background: ${style.hover};
        }
      `}
    >
      <GLIcon variant={icon} size={ICON_SIZE[size]} fill='currentColor' />
    </button>
  );
};
